import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Component, ReactNode } from 'react'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import Login from './pages/Login'
import Register from './pages/Register'
import Onboarding from './pages/Onboarding'
import ForgotPassword from './pages/ForgotPassword'
import ForgotPasswordSent from './pages/ForgotPasswordSent'
// MVP: Commented out - not in scope for MVP
// import CustomerInsights from './pages/CustomerInsights'
// import ProductInsights from './pages/ProductInsights'
import Settings from './pages/Settings'
import Alerts from './pages/Alerts'
import Profile from './pages/Profile'
import Review from './pages/Review'
// Legacy: QAConnections is no longer used - users return to /setup after OAuth
// import QAConnections from './pages/QAConnections'
// MVP: Commented out - not in scope for MVP
// import Reports from './pages/Reports'

const queryClient = new QueryClient()

// Error Boundary to catch rendering errors
class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('🚨 Error Boundary caught an error:', error)
    console.error('🚨 Error info:', errorInfo)
    console.error('🚨 Error stack:', error.stack)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', fontFamily: 'sans-serif', background: '#fff' }}>
          <h1 style={{ color: '#dc2626', fontSize: '24px', marginBottom: '16px' }}>Something went wrong</h1>
          <p style={{ color: '#991b1b', marginBottom: '8px' }}>Error: {this.state.error?.message}</p>
          <pre style={{ background: '#f5f5f5', padding: '10px', overflow: 'auto', fontSize: '12px' }}>
            {this.state.error?.stack}
          </pre>
        </div>
      )
    }

    return this.props.children
  }
}

function App() {
  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
              {/* Auth routes - publicly accessible */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/forgot-password/sent" element={<ForgotPasswordSent />} />
          
              {/* Main app routes - protected, require authentication */}
          <Route path="/" element={<Navigate to="/login" replace />} />
              <Route 
                path="/alerts" 
                element={
                  <ProtectedRoute>
                    <Layout><Alerts /></Layout>
                  </ProtectedRoute>
                } 
              />
          <Route path="/alert-center" element={<Navigate to="/alerts" replace />} />
          <Route path="/alert-center/action-tracker" element={<Navigate to="/alerts" replace />} />
            {/* MVP: Commented out - not in scope for MVP */}
              {/* <Route 
                path="/customer-insights" 
                element={
                  <ProtectedRoute>
                    <Layout><CustomerInsights /></Layout>
                  </ProtectedRoute>
                } 
              /> */}
              {/* <Route 
                path="/customer-insights/accounts" 
                element={
                  <ProtectedRoute>
                    <Layout><CustomerInsights /></Layout>
                  </ProtectedRoute>
                } 
              /> */}
              {/* <Route 
                path="/product-insights" 
                element={
                  <ProtectedRoute>
                    <Layout><ProductInsights /></Layout>
                  </ProtectedRoute>
                } 
              /> */}
              {/* <Route 
                path="/product-insights/features" 
                element={
                  <ProtectedRoute>
                    <Layout><ProductInsights /></Layout>
                  </ProtectedRoute>
                } 
              /> */}
              <Route 
                path="/setup" 
                element={
                  <ProtectedRoute>
                    <Layout><Settings /></Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <Layout><Profile /></Layout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/review" 
                element={
                  <ProtectedRoute requireReviewer={true}>
                    <Layout><Review /></Layout>
                  </ProtectedRoute>
                } 
              />
            {/* Legacy: QAConnections is no longer used - users return to /setup after OAuth */}
              {/* <Route 
                path="/qa-connections" 
                element={
                  <ProtectedRoute>
                    <Layout><QAConnections /></Layout>
                  </ProtectedRoute>
                } 
              /> */}
            {/* MVP: Commented out - not in scope for MVP */}
              {/* <Route 
                path="/reports" 
                element={
                  <ProtectedRoute>
                    <Layout><Reports /></Layout>
                  </ProtectedRoute>
                } 
              /> */}
              {/* <Route 
                path="/reports/dashboard" 
                element={
                  <ProtectedRoute>
                    <Layout><Reports /></Layout>
                  </ProtectedRoute>
                } 
              /> */}
        </Routes>
      </Router>
    </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App

